import React, { useEffect, useState, useRef } from 'react';

import { SiteNav } from '../../components/SiteNav/SiteNav';
import { Button } from '../../components/Button/Button';
import { useMediaQuery } from '../../hooks/useMediaQuery';

import type { HeroRotationItem } from './heroRotation';

import './HeroSection.css';

type HeroSectionProps = {
    heroRotation: HeroRotationItem[];
};

const ROTATION_INTERVAL_MS = 15_000;

export function HeroSection({ heroRotation }: HeroSectionProps) {
    const [currentIndex, setCurrentIndex] = useState(0);
    const isMobile = useMediaQuery('(max-width: 900px)');
    const videoRefs = useRef<Map<string, HTMLVideoElement>>(new Map());

    useEffect(() => {
        if (heroRotation.length <= 1) { return; }
        const id = window.setInterval(() => {
            setCurrentIndex(i => (i + 1) % heroRotation.length);
        }, ROTATION_INTERVAL_MS);
        return () => window.clearInterval(id);
    }, [heroRotation.length]);

    // Play only the active video; pause (and rewind) the others.
    const activeUrl = heroRotation[currentIndex]?.videoUrl;
    useEffect(() => {
        videoRefs.current.forEach((video, url) => {
            if (url === activeUrl) {
                // play() can reject (autoplay policy / interrupted by a pause) — ignore it.
                video.play().catch();
            } else {
                video.pause();
                video.currentTime = 0;
            }
        });
    }, [activeUrl]);

    return (
        <section className="hero">
            <div className="hero__frame">
                <div className="hero__nav">
                    <SiteNav />
                </div>
                {heroRotation.map((item, idx) => {
                    const src = isMobile && item.mobileVideoUrl ? item.mobileVideoUrl : item.videoUrl;
                    const posterSrc = isMobile && item.mobilePosterUrl ? item.mobilePosterUrl : item.posterUrl;
                    return src && (
                        <video
                            key={item.videoUrl}
                            ref={(el) => {
                                if (el) { videoRefs.current.set(item.videoUrl!, el); }
                                else { videoRefs.current.delete(item.videoUrl!); }
                            }}
                            className={`hero__video${idx === currentIndex ? ' hero__video--active' : ''}`}
                            src={item.videoUrl}
                            loop
                            muted
                            playsInline
                            preload={idx === currentIndex ? 'auto' : 'metadata'}
                            aria-hidden="true"
                            poster={posterSrc || undefined}
                        />
                    );
                })}
                <div className="hero__content">
                    <p className="hero__eyebrow">
                        A home for the next
                        <br />
                        generation of athletes
                    </p>
                    <h1 className="hero__heading">
                        {heroRotation.map((item, idx) => (
                            <span
                                key={item.athleteName}
                                className={`hero__heading-slot${idx === currentIndex ? ' hero__heading-slot--active' : ''}`}
                            >
                                Get {item.athleteName}&apos;s faves.
                            </span>
                        ))}
                    </h1>
                    <div className="hero__cta-stack">
                        {heroRotation.map((item, idx) => (
                            <span
                                key={item.athleteName}
                                className={`hero__cta-slot${idx === currentIndex ? ' hero__cta-slot--active' : ''}`}
                                aria-hidden={idx !== currentIndex}
                            >
                                <Button label={`Shop like ${item.athleteName}`} href={item.ctaHref} className="hero__cta" />
                            </span>
                        ))}
                    </div>
                </div>
            </div>
        </section>
    );
}
